import { useEffect, useMemo, useRef, useState } from 'react';
import {
  GroupBase,
  InputActionMeta,
  Select,
  SingleValue,
  Size,
} from 'chakra-react-select';
import { DataTestId } from 'types/general.types';
import { ChakraAutocompleteProps } from 'app/components/Core/ChakraAutocomplete/types';
import { getStyles } from './styles';
import * as overridedComponents from './overrides';
import { CLASSNAME_PREFIX, MAX_MENU_HEIGHT_PIXELS } from './constants';
import { GroupHeading } from './overrides/GroupHeading';
import { filterOption, formatOptionLabel } from './utils';
import { AutocompleteFormControl } from './AutocompleteFormControl';

export const ChakraAutocomplete = <Option extends {}>(
  props: ChakraAutocompleteProps<Option> & DataTestId
) => {
  const {
    value: valueProp,
    onChange,
    size = 'lg' as Size,
    customComponentsProps = {},
    customStyles = {},
    id,
    isRequired,
    isDisabled,
    isInvalid,
    isReadOnly,
    name,
    label,
    placeholder,
    errorMessage,
    isClearable,
    errorMessageProps,
    labelProps,
    helperTextProps,
    helperText,
    onBlur,
    onInputChange,
    menuIsOpen,
    components,
    mainRef,
    getOptionLabel,
    addFilterOption = () => false,
    inputValue: inputValueProp,
    setInputValue: setInputValueProp,
    'data-testid': dataTestId,
    ...rest
  } = props;
  const getValue = (option?: SingleValue<Option & { value?: string | null }>) =>
    option?.value === null ? null : option;
  const latestValue = useRef(valueProp);
  const filter = useRef<boolean>(false);
  const [localInputValue, setLocalInputValue] = useState(
    getOptionLabel(valueProp) || ''
  );
  const inputValue =
    inputValueProp !== undefined ? inputValueProp : localInputValue;
  const setInputValue = setInputValueProp || setLocalInputValue;
  const [value, setValue] = useState(getValue(valueProp));
  const [open, setOpen] = useState(false);
  const chakraStyles = useMemo(
    () =>
      getStyles<Option>({
        customComponents: customComponentsProps,
        customStyles,
      }),
    [customComponentsProps, customStyles]
  );

  const handleOnChange = (value: Option | null) => {
    latestValue.current = value;
    setValue(getValue(value));
    setInputValue(getOptionLabel(value) || '');
    if (onChange) {
      onChange(value);
    }
  };

  const handleOnInputChange = (value: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(value);
      if (onInputChange) {
        onInputChange(value, meta);
      }
      if (isClearable && value === '') {
        handleOnChange(null);
      }
      if (meta.prevInputValue === '' && !open) {
        setOpen(true);
      }
      filter.current = true;
    }

    if (meta.action === 'input-blur') {
      const blurredInputValue = latestValue.current
        ? getOptionLabel(latestValue.current)
        : '';

      setInputValue(blurredInputValue);

      if (onInputChange) {
        onInputChange(blurredInputValue, meta);
      }
    }
  };

  useEffect(() => {
    if (getValue(value)?.value !== getValue(valueProp)?.value) {
      handleOnChange(valueProp as Option);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueProp]);

  return (
    <AutocompleteFormControl
      formControlProps={{ id, isRequired, isDisabled, isInvalid, isReadOnly }}
      labelProps={labelProps}
      name={name}
      label={label}
      hasPlaceholder={Boolean(placeholder)}
      helperText={helperText}
      errorMessage={errorMessage}
      helperTextProps={helperTextProps}
      errorMessageProps={errorMessageProps}
    >
      <Select<Option, false, GroupBase<Option>>
        isInvalid={isInvalid}
        name={name}
        ref={mainRef}
        value={value}
        classNamePrefix={CLASSNAME_PREFIX}
        placeholder={placeholder || null}
        menuIsOpen={menuIsOpen ?? open}
        menuShouldScrollIntoView={false}
        onBlur={(e) => {
          setOpen(false);
          filter.current = false;
          if (onBlur) {
            onBlur(e);
          }
        }}
        onFocus={() => setOpen(true)}
        onChange={handleOnChange}
        menuPlacement="auto"
        maxMenuHeight={MAX_MENU_HEIGHT_PIXELS}
        chakraStyles={chakraStyles}
        getOptionLabel={getOptionLabel}
        size={size}
        formatOptionLabel={formatOptionLabel<Option>(getOptionLabel)}
        components={{ ...overridedComponents, ...components }}
        formatGroupLabel={GroupHeading}
        onInputChange={handleOnInputChange}
        inputValue={inputValue}
        customComponentsProps={customComponentsProps}
        isClearable={isClearable}
        isDisabled={isDisabled}
        controlShouldRenderValue={false}
        setInputValue={setInputValue}
        setOpen={setOpen}
        label={label}
        blurInputOnSelect
        filterOption={filterOption(getOptionLabel, addFilterOption, filter)}
        dataTestId={dataTestId}
        {...rest}
      />
    </AutocompleteFormControl>
  );
};
